import { EmployeeRole, isNotNull, useEmployeeQuery, useOrgRolesQuery } from '@alice-financial/alice-sdk'
import { useProfileTypeContext } from './SessionProfileContext'
import { AvailableProfileTypes, PROFILE_TYPES, UserProfileType } from './types'

export const isUserProfileType = (value: unknown): value is UserProfileType =>
  typeof value === 'string' && Object.keys(PROFILE_TYPES).includes(value)

/**
 * This hook will determine whether the current user has **at least one** of the required profile types available.
 * It DOES NOT automatically switch the profile type anymore. Use the setter from `useProfileTypeContext` for that.
 *
 * Return values:
 * - `undefined`: the app is still trying to determine whether the required profile type is available
 * - `false`: **none** of the required profile types are available
 * - `true`: **at least one** of the required profile types is available
 */
export const useHasProfileType = (...profileTypeOptions: UserProfileType[]): boolean | undefined => {
  const validOptions = profileTypeOptions.filter(isUserProfileType)

  const matchingProfileType = useMatchingProfileType(
    ...(validOptions.length > 0 ? (validOptions as AvailableProfileTypes) : [null])
  )
  if (validOptions.length === 0) return true

  // If still loading, return undefined
  if (matchingProfileType === undefined) return undefined

  // Returns true if a matching profile was found, false otherwise
  return Boolean(matchingProfileType)
}

export const useAvailableProfileTypes = (): AvailableProfileTypes | undefined | null => {
  const { data: orgRolesData, isLoading: isLoadingRoles, isError: isLoadingRolesError } = useOrgRolesQuery()
  const {
    data: employeeData,
    isLoading: isLoadingEmployee,
    isError: isLoadingEmployeeError,
  } = useEmployeeQuery()
  const employee = employeeData?.employee

  if (isLoadingRoles || isLoadingEmployee) return undefined
  if (isLoadingRolesError || isLoadingEmployeeError) return null
  if (!orgRolesData || !employeeData) return null

  const orgAdminRoles = orgRolesData.currentUser?.adminRoles || []
  const partnerRoles = orgRolesData.currentUser?.partnerRoles || []

  // Start with lowest priority types
  const availableProfileTypesList: UserProfileType[] = ['user', 'provisional_admin']

  if (employee) availableProfileTypesList.unshift('employee')

  if (partnerRoles.length > 0) {
    availableProfileTypesList.unshift('organization_admin')
    availableProfileTypesList.unshift('payroll_partner')
  }
  if (orgAdminRoles.length > 0) {
    // Check for specific admin roles if needed, or assume any role grants org_admin
    availableProfileTypesList.unshift('organization_admin')
    if (orgAdminRoles.length > 1) availableProfileTypesList.unshift('multi_admin')
  } else if (employee?.role) {
    // Fallback check - This logic might be deprecated if user.roles is the source of truth
    if ([EmployeeRole.EmployerAdmin, EmployeeRole.OrganizationAdmin].includes(employee.role)) {
      // Ensure no duplicates if both checks add the same roles
      if (!availableProfileTypesList.includes('employer_admin')) {
        availableProfileTypesList.unshift('employer_admin')
      }
      if (!availableProfileTypesList.includes('organization_admin')) {
        availableProfileTypesList.unshift('organization_admin')
      }
    }
  }

  // Deduplicate and cast to AvailableProfileTypes (ensures at least one element)
  // Explicitly use Array.from for potentially better linter compatibility
  const uniqueProfileTypes = Array.from(new Set(availableProfileTypesList))
  if (uniqueProfileTypes.length === 0) {
    // This should ideally not happen if 'user' is always present, but handle defensively
    return null
  }
  return uniqueProfileTypes as AvailableProfileTypes
}

/** Helper to find the first matching profile type from the available list */
export const useMatchingProfileType = (
  ...requiredProfileTypeOptions: Readonly<AvailableProfileTypes> | [null]
) => {
  const availableProfileTypes = useAvailableProfileTypes()

  if (availableProfileTypes === undefined) return undefined // still looking up what is available
  if (availableProfileTypes === null) return null // no profile types available - usually means there was an error
  if (requiredProfileTypeOptions[0] === null) return availableProfileTypes[0] // no required profile types, return the first available (prioritized)

  // Find the first required type that exists in the available list
  return (
    requiredProfileTypeOptions
      .filter(isNotNull)
      .find((profileType) => availableProfileTypes.includes(profileType)) || null
  )
}

/**
 * This hook determines the highest priority matching profile type based on the required options,
 * and automatically switches the profile type if needed.
 */

export const useRequiredProfileType = (
  ...requiredProfileTypeOptions: AvailableProfileTypes | [null]
): UserProfileType | null | undefined => {
  const { current, setCurrent } = useProfileTypeContext()

  // Call the hook unconditionally. Handle the empty case similarly to useHasProfileType.
  const matchingProfileType = useMatchingProfileType(...requiredProfileTypeOptions)

  // If no valid options were provided, technically no profile matches the *required* set.
  if (requiredProfileTypeOptions.length === 0 || requiredProfileTypeOptions[0] === null) return null

  if (matchingProfileType === undefined) return undefined
  if (!matchingProfileType) return null
  if (current === matchingProfileType) return current
  setCurrent(matchingProfileType)
  return matchingProfileType
}
